import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  getAnalyticsOverview, getAnalyticsVolume, getAnalyticsByClient,
  getAnalyticsCategories, getDeviceHealthSummary,
  getTickets, getAgents, updateTicket, getDailySpark,
} from "../api";
import { useAuth } from "../context/AuthContext";
import { ROLE_ACCESS } from "../config/roles";
import useIsMobile from "../hooks/useIsMobile";
import AnnouncementBar from "../components/AnnouncementBar";
import {
  Panel, KpiCard, StatusBadge, SLABadge, FullPageSpinner, tokens, sx,
} from "../theme";
import {
  Ticket, AlertTriangle, CheckCircle, Clock, Monitor, Plus,
  Users, BookOpen, Settings, Code, Wrench, BarChart3, Building2, ShieldCheck,
  ArrowDown, ArrowRight, TrendingUp, Award, RefreshCw, Sparkles,
} from "lucide-react";

// ── Frequency tracker (localStorage) ────────────────────────────
const FREQ_KEY = "midtc_tool_frequency";
const readFreq = () => {
  try { return JSON.parse(localStorage.getItem(FREQ_KEY) || "{}"); } catch { return {}; }
};
const bumpFreq = (key) => {
  try {
    const f = readFreq();
    f[key] = (f[key] || 0) + 1;
    localStorage.setItem(FREQ_KEY, JSON.stringify(f));
  } catch {}
};

// ── Utility formatters ──────────────────────────────────────────
const fmtTime = (d) => new Date(d).toLocaleDateString("en-US", { month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" });
const fmtMins = (m) => {
  if (!m && m !== 0) return "—";
  if (m < 60) return `${Math.round(m)}m`;
  if (m < 1440) return `${(m / 60).toFixed(1)}h`;
  return `${(m / 1440).toFixed(1)}d`;
};
const greet = () => {
  const h = new Date().getHours();
  if (h < 5)  return "Working late";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  if (h < 22) return "Good evening";
  return "Working late";
};

// ── Sparkline (volume over time) ─────────────────────────────────
const Sparkline = ({ data, color = "var(--t-accent)", height = 110 }) => {
  if (!data || data.length === 0) {
    return <div style={{ height, display:"flex", alignItems:"center", justifyContent:"center", color:"var(--t-text-faint)", fontSize:12 }}>No data</div>;
  }
  const max = Math.max(...data.map(d => d.count), 1);
  const w = 100, h = 100; // viewBox space
  const pts = data.map((d, i) => {
    const x = (i / (data.length - 1 || 1)) * w;
    const y = h - (d.count / max) * h;
    return [x, y];
  });
  const linePath = pts.map(([x,y], i) => `${i===0?"M":"L"}${x},${y}`).join(" ");
  const areaPath = `${linePath} L${w},${h} L0,${h} Z`;
  return (
    <div style={{ padding:"10px 16px 14px" }}>
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width:"100%", height }}>
        <defs>
          <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" style={{ stopColor: color, stopOpacity: 0.35 }}/>
            <stop offset="100%" style={{ stopColor: color, stopOpacity: 0 }}/>
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#sparkFill)" />
        <path d={linePath} fill="none" style={{ stroke: color }} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        {pts.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="1.2" style={{ fill: color }} vectorEffect="non-scaling-stroke" />
        ))}
      </svg>
      <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:"var(--t-text-faint)", marginTop:6 }}>
        <span>{data[0]?.date && new Date(data[0].date).toLocaleDateString("en-US", { month:"short", day:"numeric" })}</span>
        <span>{data.reduce((s, d) => s + Number(d.count || 0), 0)} total · peak {max}</span>
        <span>{data[data.length - 1]?.date && new Date(data[data.length - 1].date).toLocaleDateString("en-US", { month:"short", day:"numeric" })}</span>
      </div>
    </div>
  );
};

// ── Donut chart ──────────────────────────────────────────────────
const Donut = ({ slices, total, label }) => {
  const r = 36, c = 50, stroke = 14;
  const circumference = 2 * Math.PI * r;
  const sum = total ?? slices.reduce((s, x) => s + x.value, 0);
  let offset = 0;
  return (
    <div style={{ padding:"14px 16px", display:"flex", alignItems:"center", gap:18 }}>
      <svg width="110" height="110" viewBox="0 0 100 100" style={{ flexShrink:0 }}>
        <circle cx={c} cy={c} r={r} fill="none" stroke="rgba(148,163,184,0.1)" strokeWidth={stroke} />
        {slices.filter(s => s.value > 0).map((slice, i) => {
          const frac = slice.value / sum;
          const dash = frac * circumference;
          const el = (
            <circle key={i} cx={c} cy={c} r={r} fill="none"
              stroke={slice.color} strokeWidth={stroke}
              strokeDasharray={`${dash} ${circumference - dash}`}
              strokeDashoffset={-offset}
              transform={`rotate(-90 ${c} ${c})`}
              strokeLinecap="butt"
            />
          );
          offset += dash;
          return el;
        })}
        <text x={c} y={c - 2} textAnchor="middle" fontSize="16" fontWeight="800" fill="#f1f5f9">{sum}</text>
        <text x={c} y={c + 11} textAnchor="middle" fontSize="7" fill="#56677e" fontWeight="600" letterSpacing="0.05em">{label?.toUpperCase()}</text>
      </svg>
      <div style={{ flex:1, display:"flex", flexDirection:"column", gap:6, minWidth:0 }}>
        {slices.map((s, i) => (
          <div key={i} style={{ display:"flex", alignItems:"center", gap:8, fontSize:12 }}>
            <span style={{ width:8, height:8, borderRadius:2, background:s.color, flexShrink:0 }}/>
            <span style={{ color:"var(--t-text-body)", flex:1, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{s.label}</span>
            <span style={{ color:"var(--t-text-muted)", fontWeight:600 }}>{s.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── SLA Gauge ────────────────────────────────────────────────────
const Gauge = ({ percent, label, sub }) => {
  const pct = Math.max(0, Math.min(100, percent));
  const color = pct >= 90 ? "#34d399" : pct >= 75 ? "#fbbf24" : "#f87171";
  const r = 38, c = 50;
  const circumference = Math.PI * r; // half circle
  const dash = (pct / 100) * circumference;
  return (
    <div style={{ padding:"14px 16px", display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}>
      <svg width="170" height="100" viewBox="0 0 100 60">
        <path d={`M ${c-r} ${c} A ${r} ${r} 0 0 1 ${c+r} ${c}`} fill="none" stroke="rgba(148,163,184,0.12)" strokeWidth="9" strokeLinecap="round" />
        <path d={`M ${c-r} ${c} A ${r} ${r} 0 0 1 ${c+r} ${c}`} fill="none" stroke={color} strokeWidth="9" strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference - dash}`} />
        <text x={c} y={c - 6} textAnchor="middle" fontSize="18" fontWeight="800" fill={color}>{pct.toFixed(0)}%</text>
      </svg>
      <div style={{ fontSize:11, fontWeight:700, color:"var(--t-text-label)", textTransform:"uppercase", letterSpacing:"0.06em" }}>{label}</div>
      {sub && <div style={{ fontSize:11, color:"var(--t-text-faint)" }}>{sub}</div>}
    </div>
  );
};

// ── Quick Tool Card (frequency-tracked) ─────────────────────────
const QuickTool = ({ tool, count, onClick }) => {
  const Icon = tool.icon;
  return (
    <button onClick={onClick} title={tool.label} style={{
      display:"flex", flexDirection:"column", alignItems:"flex-start", gap:8,
      padding:"12px 14px", borderRadius:10, cursor:"pointer", textAlign:"left",
      background:"var(--t-surface-mid)",
      border:"1px solid var(--t-border-soft)",
      transition:"all 0.15s", fontFamily:"inherit", color:"var(--t-text-body)",
      position:"relative", overflow:"hidden", minHeight:78,
    }}
    onMouseEnter={e => { e.currentTarget.style.borderColor="var(--t-accent-soft)"; e.currentTarget.style.transform="translateY(-1px)"; }}
    onMouseLeave={e => { e.currentTarget.style.borderColor="var(--t-border-soft)"; e.currentTarget.style.transform="translateY(0)"; }}>
      <div style={{ width:30, height:30, borderRadius:7, background:`color-mix(in srgb, ${tool.color} 13%, transparent)`,
        border:`1px solid color-mix(in srgb, ${tool.color} 25%, transparent)`,
        display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
        <Icon size={15} style={{ color: tool.color }}/>
      </div>
      <div style={{ fontSize:12, fontWeight:600, color:"var(--t-text-primary)", lineHeight:1.3 }}>{tool.label}</div>
      {count > 0 && (
        <span style={{ position:"absolute", top:8, right:8, fontSize:9, color:"var(--t-text-faint)",
          background:"rgba(148,163,184,0.08)", padding:"1px 6px", borderRadius:99, fontWeight:600 }}>
          {count}×
        </span>
      )}
    </button>
  );
};

// ── Tool catalog (filtered by role access) ──────────────────────
const ALL_TOOLS = [
  { key:"new-ticket",     label:"New Ticket",       icon:Plus,         color:"var(--t-accent)", path:"/tickets?new=1",  page:"tickets"        },
  { key:"all-tickets",    label:"All Tickets",      icon:Ticket,       color:"#60a5fa", path:"/tickets",         page:"tickets"        },
  { key:"sla-breaches",   label:"SLA Breaches",     icon:AlertTriangle,color:"#f87171", path:"/tickets?sla_status=breach", page:"tickets" },
  { key:"devices",        label:"Devices",          icon:Monitor,      color:"#34d399", path:"/devices",         page:"devices"        },
  { key:"critical-dev",   label:"Critical Devices", icon:AlertTriangle,color:"#f87171", path:"/devices?status=critical", page:"devices" },
  { key:"clients",        label:"Clients",          icon:Building2,    color:"var(--badge-amber)", path:"/clients",         page:"clients"        },
  { key:"edr-console",    label:"EDR Console",      icon:ShieldCheck,  color:"#a78bfa", path:"/edr-console",     page:"devices"        },
  { key:"edr-inventory",  label:"EDR Inventory",    icon:Monitor,      color:"#a78bfa", path:"/edr-inventory",   page:"devices"        },
  { key:"dev-queue",      label:"Dev Queue",        icon:Code,         color:"#c084fc", path:"/dev-tickets",     page:"dev-tickets"    },
  { key:"infrastructure", label:"Infrastructure",   icon:Wrench,       color:"#fb923c", path:"/infrastructure",  page:"infrastructure" },
  { key:"analytics",      label:"Analytics",        icon:BarChart3,    color:"#22d3ee", path:"/analytics",       page:"analytics"      },
  { key:"agents",         label:"Agents",           icon:Users,        color:"#f472b6", path:"/agents",          page:"agents"         },
  { key:"knowledge",      label:"Knowledge Base",   icon:BookOpen,     color:"#34d399", path:"/knowledge-base",  page:"knowledge-base" },
  { key:"settings",       label:"Settings",         icon:Settings,     color:"var(--t-accent-2)", path:"/settings",        page:"settings"       },
];

// ────────────────────────────────────────────────────────────────
//   Main Dashboard
// ────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { agent } = useAuth();
  const [overview,   setOverview]   = useState(null);
  const [volume,     setVolume]     = useState([]);
  const [byClient,   setByClient]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [deviceSum,  setDeviceSum]  = useState(null);
  const [recent,     setRecent]     = useState([]);
  const [agents,     setAgents]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [freq, setFreq] = useState(readFreq());
  const [spark, setSpark] = useState(null);   // today's quote or uplifting line — the same one as the 8:30 AM email

  const allowedPages = ROLE_ACCESS[agent?.role] || [];
  const isPriv = ["admin","manager"].includes(agent?.role);

  // Tools: filter by role, sort by frequency desc, fall back to alphabetical
  const tools = useMemo(() => {
    return ALL_TOOLS
      .filter(t => allowedPages.includes(t.page))
      .map(t => ({ ...t, count: freq[t.key] || 0 }))
      .sort((a, b) => (b.count - a.count) || a.label.localeCompare(b.label));
  }, [allowedPages, freq]);

  const handleToolClick = useCallback((tool) => {
    bumpFreq(tool.key);
    setFreq(readFreq());
    navigate(tool.path);
  }, [navigate]);

  // ── Data load ───────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    try {
      const [ov, vol, cats, dev, tk, ag] = await Promise.all([
        getAnalyticsOverview().catch(() => null),
        getAnalyticsVolume().catch(() => ({ data: [] })),
        getAnalyticsCategories().catch(() => ({ data: [] })),
        getDeviceHealthSummary().catch(() => null),
        getTickets({ limit: 8, page: 1, status: "Active" }).catch(() => ({ data: { tickets: [] } })),
        getAgents().catch(() => ({ data: [] })),
      ]);
      if (ov?.data)  setOverview(ov.data);
      if (vol?.data) setVolume(vol.data);
      if (cats?.data) setCategories(cats.data);
      if (dev?.data) setDeviceSum(dev.data);
      if (tk?.data?.tickets) setRecent(tk.data.tickets);
      if (ag?.data) setAgents(ag.data);
      // by-client is admin/manager only
      if (isPriv) {
        try { const bc = await getAnalyticsByClient(); setByClient(bc.data || []); } catch {}
      }
    } finally { setLoading(false); }
  }, [isPriv]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { getDailySpark().then(r => setSpark(r.data?.text ? r.data : null)).catch(() => {}); }, []);
  useEffect(() => {
    const id = setInterval(fetchAll, 60_000);
    return () => clearInterval(id);
  }, [fetchAll]);

  const handleAssign = async (e, ticketId) => {
    e.stopPropagation();
    const agentId = e.target.value === "__unassigned__" ? null : e.target.value;
    try {
      await updateTicket(ticketId, { assigned_to: agentId });
      const a = agentId ? agents.find(a => String(a.id) === String(agentId)) : null;
      setRecent(prev => prev.map(t => t.id === ticketId ? { ...t, assigned_to: agentId, assigned_to_name: a ? `${a.first_name} ${a.last_name}` : null } : t));
    } catch {}
  };

  // ── Derived KPIs ────────────────────────────────────────────
  const t = overview?.tickets || {};
  const d = overview?.devices  || {};
  const activeCount = (Number(t.open) || 0) + (Number(t.in_progress) || 0) + (Number(t.on_hold) || 0);
  const totalDevices = (Number(d.total) || (deviceSum?.total ?? 0));
  const criticalDevices = Number(d.critical) || 0;
  const offlineDevices = Number(d.offline) || 0;
  const slaBreaches = Number(t.sla_breaches) || 0;
  const avgFrr = t.avg_first_response_mins != null ? Number(t.avg_first_response_mins) : null;

  // SLA percent: completed - breaches / completed
  const totalTk = Number(t.total) || 0;
  const slaPct = totalTk > 0 ? Math.max(0, Math.min(100, (1 - slaBreaches / totalTk) * 100)) : 100;

  // Volume delta (today vs avg of previous 7 days)
  const today = volume[volume.length - 1]?.count ? Number(volume[volume.length - 1].count) : 0;
  const prev7Avg = (() => {
    const slice = volume.slice(-8, -1);
    if (!slice.length) return 0;
    return slice.reduce((s, x) => s + Number(x.count || 0), 0) / slice.length;
  })();
  const todayDelta = Math.round(today - prev7Avg);

  // Last 14 days only for the sparkline
  const sparkData = useMemo(() => volume.slice(-14), [volume]);

  // Priority donut: derived from tickets data — we don't have a prebuilt priority breakdown,
  // so build from agentStats if missing or fall back to category data
  const priorityDonut = useMemo(() => {
    // fall back: build from categories array (top 4)
    const palette = ["#f87171","#fb923c","#fbbf24","#34d399","#60a5fa","#a78bfa","#22d3ee","#f472b6"];
    return categories.slice(0, 6).map((c, i) => ({
      label: c.category || "Uncategorized",
      value: Number(c.open_count || 0),
      color: palette[i % palette.length],
    }));
  }, [categories]);

  // Top clients (open tickets) — privileged users only
  const topClients = useMemo(() =>
    [...byClient]
      .sort((a, b) => Number(b.open_count || 0) - Number(a.open_count || 0))
      .slice(0, 6),
  [byClient]);

  // Agent leaderboard
  const leaderboard = useMemo(() => {
    const stats = overview?.agentStats || [];
    return [...stats].sort((a, b) => Number(b.resolved || 0) - Number(a.resolved || 0)).slice(0, 6);
  }, [overview]);

  // ── Loading state ───────────────────────────────────────────
  if (loading) return <FullPageSpinner />;

  return (
    <div style={{ padding: isMobile ? "14px 14px 24px" : "22px 22px 32px", maxWidth:1600 }} className="fade-in">

      {/* Greeting + page header */}
      <div style={{ paddingBottom:14, borderBottom:"1px solid var(--t-border-soft)", marginBottom:18,
        display:"flex", alignItems:"flex-end", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:800, color:"var(--t-text-primary)", letterSpacing:"-0.5px" }}>
            {greet()}{agent?.firstName ? `, ${agent.firstName}` : ""} <span style={{ opacity:0.4 }}>·</span> Dashboard
          </h1>
          <p style={{ color:"var(--t-text-faint)", fontSize:13, marginTop:3 }}>
            {new Date().toLocaleDateString("en-US", { weekday:"long", month:"long", day:"numeric", year:"numeric" })}
            <span style={{ margin:"0 8px" }}>·</span>{activeCount} active tickets · {totalDevices} devices monitored
          </p>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={fetchAll} style={{
            display:"inline-flex", alignItems:"center", gap:7, padding:"9px 14px", borderRadius:8,
            background:"var(--t-panel)", color:"var(--t-text-muted)",
            border:"1px solid var(--t-border-mid)", fontSize:13, fontWeight:500, cursor:"pointer",
          }}>
            <RefreshCw size={13}/>
          </button>
          <button onClick={() => { bumpFreq("new-ticket"); setFreq(readFreq()); navigate("/tickets?new=1"); }} style={{
            display:"inline-flex", alignItems:"center", gap:7, padding:"9px 18px", borderRadius:8,
            background:"var(--t-accent-grad)", color:"#fff",
            border:"none", fontSize:13, cursor:"pointer",
            fontFamily:"var(--t-font-btn, inherit)", fontWeight:"var(--t-btn-weight, 700)",
            textTransform:"var(--t-btn-transform, none)", letterSpacing:"var(--t-btn-letter, 0)",
            boxShadow:"var(--t-btn-shadow, var(--t-accent-glow))",
          }}>
            <Plus size={15}/> New Ticket
          </button>
        </div>
      </div>

      {/* System announcements (Admin Review → Announcements) — until acknowledged */}
      <AnnouncementBar style={{ marginBottom:18 }} />

      {/* Today's spark: a quote or an uplifting line, different for every tech, new every day */}
      {spark && (
        <div style={{ display:"flex", gap:14, alignItems:"flex-start", padding:"14px 18px", marginBottom:18, borderRadius:12,
          background:"var(--t-panel)", border:"1px solid var(--t-border-soft)", borderLeft:"3px solid var(--t-accent)" }}>
          <Sparkles size={18} style={{ color:"var(--t-accent)", flexShrink:0, marginTop:3 }} />
          <div style={{ minWidth:0 }}>
            <div style={{ fontSize:11, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", color:"var(--t-text-faint)" }}>
              {spark.kind === "quote" ? "Quote of the day" : "Today's boost"}
            </div>
            <div style={{ fontSize:15.5, lineHeight:1.55, color:"var(--t-text-primary)", marginTop:4,
              fontStyle: spark.kind === "quote" ? "italic" : "normal", fontWeight: spark.kind === "quote" ? 500 : 600 }}>
              {spark.kind === "quote" ? `\u201c${spark.text}\u201d` : spark.text}
            </div>
            {spark.kind === "quote" && spark.author && (
              <div style={{ fontSize:12.5, color:"var(--t-text-faint)", marginTop:5 }}>
                — {spark.author}{spark.source ? <>, <i>{spark.source}</i></> : null}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hero KPIs */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(170px,1fr))", gap:10, marginBottom:18 }}>
        <KpiCard label="Active Tickets"   value={activeCount}      icon={Ticket}        color="var(--t-accent)"
          delta={todayDelta} sub={`Today: ${today} new`}
          onClick={() => { bumpFreq("all-tickets"); setFreq(readFreq()); navigate("/tickets?status=Active"); }} />
        <KpiCard label="SLA Breaches"     value={slaBreaches}      icon={AlertTriangle} color="#f87171"
          sub="Needs attention"
          onClick={() => { bumpFreq("sla-breaches"); setFreq(readFreq()); navigate("/tickets?sla_status=breach"); }} />
        <KpiCard label="Avg First Resp"   value={fmtMins(avgFrr)}  icon={Clock}         color="#fbbf24"
          sub={avgFrr != null ? "Across all tickets" : "Not enough data"} />
        <KpiCard label="Resolved"         value={Number(t.resolved) || 0} icon={CheckCircle} color="#34d399"
          sub="Lifetime"
          onClick={() => navigate("/tickets?status=Resolved")} />
        <KpiCard label="Critical Devices" value={criticalDevices}  icon={Monitor}       color="#f87171"
          sub={offlineDevices ? `${offlineDevices} offline` : "All connected"}
          onClick={() => { bumpFreq("critical-dev"); setFreq(readFreq()); navigate("/devices?status=critical"); }} />
        <KpiCard label="Healthy Devices"  value={Number(d.healthy) || 0} icon={ShieldCheck} color="#22d3ee"
          sub={totalDevices ? `${Math.round((Number(d.healthy) || 0) / totalDevices * 100)}% of fleet` : "—"}
          onClick={() => navigate("/devices")} />
      </div>

      {/* Quick Tools + Recent Activity */}
      <div style={{ display:"grid", gridTemplateColumns:"minmax(0,2fr) minmax(0,1fr)", gap:14, marginBottom:18 }}>

        {/* Quick Tools */}
        <Panel title="Quick Tools" action={
          <span style={{ fontSize:11, color:"var(--t-text-faint)" }}>Reorders by your usage</span>
        }>
          <div style={{ padding:14, display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))", gap:10 }}>
            {tools.length === 0 ? (
              <div style={{ padding:24, textAlign:"center", color:"var(--t-text-faint)", fontSize:13, gridColumn:"1/-1" }}>No tools available for your role.</div>
            ) : tools.map(tool => (
              <QuickTool key={tool.key} tool={tool} count={tool.count}
                onClick={() => handleToolClick(tool)} />
            ))}
          </div>
        </Panel>

        {/* Activity feed */}
        <Panel title="Live Activity" action={
          <span style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:11, color:"#34d399" }}>
            <span style={{ width:6, height:6, borderRadius:"50%", background:"#34d399", boxShadow:"0 0 6px #34d399" }}/>
            Live
          </span>
        }>
          <div style={{ padding:"10px 14px", display:"flex", flexDirection:"column", gap:8, maxHeight:380, overflowY:"auto" }}>
            {recent.length === 0 && <div style={{ padding:20, textAlign:"center", color:"var(--t-text-faint)", fontSize:12 }}>No recent activity.</div>}
            {recent.slice(0, 8).map(tk => (
              <div key={tk.id} onClick={() => navigate(`/tickets/${tk.id}`)}
                style={{ display:"flex", gap:10, padding:"8px 10px", borderRadius:8, cursor:"pointer", transition:"background 0.12s",
                  background:"var(--t-surface-mid)", border:"1px solid var(--t-border-soft)" }}
                onMouseEnter={e => { e.currentTarget.style.background="var(--t-accent-softer)"; e.currentTarget.style.borderColor="var(--t-accent-soft)"; }}
                onMouseLeave={e => { e.currentTarget.style.background="var(--t-surface-mid)"; e.currentTarget.style.borderColor="var(--t-border-soft)"; }}>
                <div style={{ width:6, height:6, borderRadius:"50%", marginTop:6, flexShrink:0,
                  background: tk.sla_status === "breach" ? "#ef4444" : tk.priority === "Critical" ? "#fb923c" : "var(--t-accent)",
                  boxShadow: tk.sla_status === "breach" ? "0 0 6px #ef4444" : "none" }}/>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2 }}>
                    <span style={{ fontSize:11, color:"#60a5fa", fontFamily:"monospace", fontWeight:600 }}>{tk.ticket_number}</span>
                    <SLABadge status={tk.sla_status}/>
                  </div>
                  <div style={{ fontSize:12, color:"var(--t-text-body)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{tk.subject}</div>
                  <div style={{ fontSize:10, color:"var(--t-text-faint)", marginTop:2 }}>{tk.client_name || "—"} · {fmtTime(tk.last_activity_at || tk.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {/* Charts row */}
      <div style={{ display:"grid", gridTemplateColumns:"minmax(0,2fr) minmax(0,1.2fr) minmax(0,1fr)", gap:14, marginBottom:18 }}>
        <Panel title="Ticket Volume · Last 14 Days" action={
          <span style={{ display:"inline-flex", alignItems:"center", gap:4, fontSize:11,
            color: todayDelta > 0 ? "#fca5a5" : todayDelta < 0 ? "#86efac" : "#94a3b8" }}>
            {todayDelta > 0 ? <TrendingUp size={11}/> : todayDelta < 0 ? <ArrowDown size={11}/> : <ArrowRight size={11}/>}
            {todayDelta > 0 ? "+" : ""}{todayDelta} vs 7-day avg
          </span>
        }>
          <Sparkline data={sparkData} color="var(--t-accent)"/>
        </Panel>

        <Panel title="Open by Category">
          {priorityDonut.length === 0
            ? <div style={{ padding:30, textAlign:"center", color:"var(--t-text-faint)", fontSize:12 }}>No open tickets.</div>
            : <Donut slices={priorityDonut} label="Open"/>
          }
        </Panel>

        <Panel title="SLA Compliance">
          <Gauge percent={slaPct} label="In SLA"
            sub={slaBreaches ? `${slaBreaches} breach${slaBreaches !== 1 ? "es" : ""}` : "All within target"}/>
        </Panel>
      </div>

      {/* Top Clients + Agent Leaderboard */}
      {(isPriv || leaderboard.length > 0) && (
        <div style={{ display:"grid", gridTemplateColumns:(isPriv && !isMobile) ? "minmax(0,1fr) minmax(0,1fr)" : "1fr", gap:14, marginBottom:18 }}>
          {isPriv && (
            <Panel title="Top Clients · Open Tickets" action={
              <button onClick={() => navigate("/clients")} style={{ background:"none", border:"none", color:"var(--t-accent)", fontSize:11, cursor:"pointer", fontFamily:"inherit", display:"inline-flex", alignItems:"center", gap:3 }}>
                All clients <ArrowRight size={11}/>
              </button>
            }>
              <div style={{ padding:"4px 0" }}>
                {topClients.length === 0 && <div style={{ padding:20, textAlign:"center", color:"var(--t-text-faint)", fontSize:12 }}>No client data.</div>}
                {topClients.map((c, i) => {
                  const max = Math.max(...topClients.map(x => Number(x.open_count) || 0), 1);
                  const pct = ((Number(c.open_count) || 0) / max) * 100;
                  return (
                    <div key={i} style={{ padding:"9px 16px", borderTop:i>0?"1px solid rgba(148,163,184,0.08)":"none" }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:5 }}>
                        <span style={{ fontSize:13, color:"var(--t-text-body)", fontWeight:500, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.client_name || c.company_name}</span>
                        <span style={{ fontSize:12, color:"var(--t-text-muted)", fontWeight:600 }}>{c.open_count || 0} open</span>
                      </div>
                      <div style={{ height:4, background:"rgba(148,163,184,0.1)", borderRadius:99, overflow:"hidden" }}>
                        <div style={{ height:"100%", width:`${pct}%`, background:"var(--t-accent-grad)", borderRadius:99 }}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Panel>
          )}

          <Panel title="Agent Leaderboard · Resolved" action={<Award size={13} color="#fbbf24"/>}>
            <div style={{ padding:"4px 0" }}>
              {leaderboard.length === 0 && <div style={{ padding:20, textAlign:"center", color:"var(--t-text-faint)", fontSize:12 }}>No agent stats yet.</div>}
              {leaderboard.map((a, i) => {
                const initials = (a.name || "").split(" ").map(p => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
                const medal = i === 0 ? "#fbbf24" : i === 1 ? "#94a3b8" : i === 2 ? "#fb923c" : null;
                return (
                  <div key={a.id || i} style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 16px", borderTop:i>0?"1px solid rgba(148,163,184,0.08)":"none" }}>
                    <span style={{ width:18, fontSize:11, color:medal || "#56677e", fontWeight:700, textAlign:"center" }}>
                      {i + 1}
                    </span>
                    <span style={{ width:30, height:30, borderRadius:"50%", flexShrink:0,
                      background:"linear-gradient(135deg, color-mix(in srgb, var(--t-accent) 45%, #0b1020), var(--t-accent))", color:"#fff",
                      display:"inline-flex", alignItems:"center", justifyContent:"center",
                      fontSize:11, fontWeight:700,
                      boxShadow: medal ? `0 0 10px ${medal}40` : "none",
                      border: medal ? `1px solid ${medal}80` : "none" }}>
                      {initials || "?"}
                    </span>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, color:"var(--t-text-primary)", fontWeight:500, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{a.name || "—"}</div>
                      <div style={{ fontSize:10, color:"var(--t-text-faint)" }}>{a.assigned_tickets || 0} assigned</div>
                    </div>
                    <div style={{ fontSize:14, fontWeight:700, color:"#34d399" }}>{a.resolved || 0}</div>
                  </div>
                );
              })}
            </div>
          </Panel>
        </div>
      )}

      {/* Recent tickets */}
      <Panel title="Recent Tickets" action={
        <button onClick={() => navigate("/tickets")} style={{ background:"none", border:"none", color:"var(--t-accent)", fontSize:11, cursor:"pointer", fontFamily:"inherit", display:"inline-flex", alignItems:"center", gap:3 }}>
          View all <ArrowRight size={11}/>
        </button>
      }>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr style={{ borderBottom:"1px solid var(--t-border-soft)" }}>
                {["Ticket #","Customer","Submitter","Subject","Assigned To","Submitted","Status","SLA"].map(h => (
                  <th key={h} style={{ padding:"10px 14px", textAlign:"left", fontSize:10, color:"var(--t-text-faint)", fontWeight:700,
                    textTransform:"uppercase", letterSpacing:"0.06em", whiteSpace:"nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 && (
                <tr><td colSpan={8} style={{ padding:40, textAlign:"center", color:"var(--t-text-faint)", fontSize:13 }}>No recent tickets.</td></tr>
              )}
              {recent.map(tk => (
                <tr key={tk.id} onClick={() => navigate(`/tickets/${tk.id}`)}
                  style={{ borderBottom:"1px solid rgba(148,163,184,0.06)", cursor:"pointer", transition:"background 0.1s" }}
                  onMouseEnter={e => e.currentTarget.style.background="var(--t-accent-softer)"}
                  onMouseLeave={e => e.currentTarget.style.background="transparent"}>
                  <td style={{ padding:"11px 14px", color:"#60a5fa", fontFamily:"monospace", fontSize:12, fontWeight:600, whiteSpace:"nowrap" }}>{tk.ticket_number}</td>
                  <td style={{ padding:"11px 14px", color:"var(--t-text-body)", fontSize:13, whiteSpace:"nowrap" }}>{tk.client_name || "—"}</td>
                  <td style={{ padding:"11px 14px", color:"var(--t-text-muted)", fontSize:13, whiteSpace:"nowrap" }}>{tk.submitter_name || "—"}</td>
                  <td style={{ padding:"11px 14px", color:"var(--t-text-body)", fontSize:13, maxWidth:280, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{tk.subject}</td>
                  <td style={{ padding:"8px 14px", whiteSpace:"nowrap" }} onClick={e => e.stopPropagation()}>
                    <select value={tk.assigned_to ?? "__unassigned__"} onChange={e => handleAssign(e, tk.id)}
                      style={{ background:"var(--t-card-input)", border:"1px solid rgba(99,162,255,0.3)", borderRadius:6,
                        padding:"5px 8px", color: tk.assigned_to ? "var(--t-text-body)" : "var(--t-text-faint)",
                        fontSize:12, cursor:"pointer", maxWidth:140 }}>
                      <option value="__unassigned__">Unassigned</option>
                      {agents.filter(a => a.is_active !== false).map(a => (
                        <option key={a.id} value={a.id}>{a.first_name} {a.last_name}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding:"11px 14px", color:"var(--t-text-faint)", fontSize:11, whiteSpace:"nowrap" }}>{fmtTime(tk.created_at)}</td>
                  <td style={{ padding:"11px 14px" }}><StatusBadge status={tk.status}/></td>
                  <td style={{ padding:"11px 14px" }}><SLABadge status={tk.sla_status}/></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

    </div>
  );
}
